import React, { useRef, useState, useEffect } from "react";
import { getTeachersBySchoolId } from "../services/APIClient";
import ShareTeachersModal from "./ShareTeachersModal";

const ShareActionCard = ({
  cardPosition,
  handleCloseCard,
  activeAccordionRef,
  activeAccordionStudents,
  setViewParent,
  setViewTeacher,
  assessmentName,
  className,
  rollNumber,
  schoolid,
  senderName,
  teacherId,
  assessmentIds,
  lockSelection = false,
}) => {
  const shareCardRef = useRef(null);
  const modalRef = useRef(null);

  // Select student state management
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Modal state management
  const [showTeachersModal, setShowTeachersModal] = useState(false);

  // View mode state
  const [isParentView, setIsParentView] = useState(false);
  const [isTeacherView, setIsTeacherView] = useState(false);
  const [isDetailedView, setIsDetailedView] = useState(true); // Default to teacher view

  // Reset states when activeAccordionStudents changes
  useEffect(() => {
    if (activeAccordionStudents && activeAccordionStudents.length > 0) {
      setSearchQuery("");
      if (!lockSelection) {
        setSelectedStudentIds([]);
        setSelectAll(false);
      }
    }
  }, [activeAccordionStudents]);

  // Pre-select all students when lockSelection is true
  useEffect(() => {
    if (lockSelection && activeAccordionStudents?.length > 0) {
      setSelectedStudentIds(activeAccordionStudents.map((s) => s.id));
      setSelectAll(true);
    }
  }, [lockSelection, activeAccordionStudents]);

  // Click outside handler to close the action card
  useEffect(() => {
    function handleClickOutside(event) {
      // Don't close if clicking on the modal or its children
      const modalElement = document.querySelector(".modal");
      if (modalElement && modalElement.contains(event.target)) {
        return;
      }

      // Close the action card if clicking outside it
      if (
        shareCardRef.current &&
        !shareCardRef.current.contains(event.target)
      ) {
        handleCloseCard();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseCard]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudentIds((prevSelected) =>
      prevSelected.includes(studentId)
        ? prevSelected.filter((id) => id !== studentId)
        : [...prevSelected, studentId]
    );
    setSearchQuery(""); // Clear the search query after selection
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(
        activeAccordionStudents.map((student) => student.id)
      );
    }
    setSelectAll(!selectAll);
  };

  // Get filtered students based on search query
  const filteredStudents = activeAccordionStudents.filter((student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleShareParent = () => {
    // Update parent component state
    setViewParent(true);
    setViewTeacher(false);

    // Update local state
    setIsParentView(true);
    setIsTeacherView(false);
    setIsDetailedView(false); // Parent view is NOT detailed

    // Show modal after state is updated
    setShowTeachersModal(true);
  };

  const handleShareTeacher = () => {
    // Update parent component state
    setViewParent(false);
    setViewTeacher(true);

    // Update local state
    setIsParentView(false);
    setIsTeacherView(true);
    setIsDetailedView(true); // Teacher view IS detailed

    // Show modal after state is updated
    setShowTeachersModal(true);
  };

  // Close modal handler
  const handleModalClose = () => {
    setShowTeachersModal(false);
  };

  return (
    <>
      <div
        ref={shareCardRef}
        className="action-card"
        style={{
          position: "absolute",
          top: `${cardPosition.top}px`,
          left: `${cardPosition.left}px`,
          zIndex: 1000,
          width: "320px",
        }}
      >
        <div className="action-card-header">
          <p className="action-header">Share</p>
          <button
            onClick={() => {
              handleCloseCard();
            }}
            className="close-button"
          >
            ✕
          </button>
        </div>

        <div className="action-card-body">
          <p className="action-body-text">Share Report Cards</p>
          {!lockSelection && (
            <>
              <div className="action-search-bar">
                <img
                  src={process.env.PUBLIC_URL + "/assets/imgs/search-outline.svg"}
                  alt="Search"
                  className="search-icon"
                />
                <input
                  type="text"
                  placeholder="Search student"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    background: "transparent",
                  }}
                />
              </div>

              {searchQuery && (
                <div className="search-results">
                  {filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className="search-result-item"
                      onClick={() => handleStudentSelect(student.id)}
                      style={{
                        backgroundColor: selectedStudentIds.includes(student.id)
                          ? "#e0e0e0"
                          : "transparent",
                      }}
                    >
                      {student.name}
                    </div>
                  ))}
                </div>
              )}
              <div className="select-all-container">
                <input
                  type="checkbox"
                  id="selectAll"
                  name="selectAll"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <label htmlFor="selectAll">View All Students</label>
              </div>
            </>
          )}
          {selectedStudentIds.length > 0 && (
            <div className="selected-students">
              {selectedStudentIds.map((studentId) => {
                const student = activeAccordionStudents.find(
                  (student) => student.id === studentId
                );
                return student ? (
                  <div key={studentId} className="selected-student-item">
                    {!lockSelection && (
                      <input
                        type="checkbox"
                        checked={selectedStudentIds.includes(studentId)}
                        onChange={() => handleStudentSelect(studentId)}
                      />
                    )}
                    <label>{student.name}</label>
                  </div>
                ) : null;
              })}
            </div>
          )}
          <div className="action-button-container">
            <button
              className={`blue-action-button ${
                selectedStudentIds.length === 0 ? "disabled-button" : ""
              }`}
              disabled={selectedStudentIds.length === 0}
              onClick={handleShareParent}
            >
              Share Simple
            </button>
            <button
              className={`blue-action-button ${
                selectedStudentIds.length === 0 ? "disabled-button" : ""
              }`}
              disabled={selectedStudentIds.length === 0}
              onClick={handleShareTeacher}
            >
              Share Detailed
            </button>
          </div>
        </div>
      </div>

      {/* ShareTeachersModal - rendered separately to avoid click handler issues */}
      <ShareTeachersModal
        show={showTeachersModal}
        handleClose={handleModalClose}
        assessmentName={assessmentName}
        className={className}
        rollNumber={rollNumber}
        senderName={senderName}
        teacherId={teacherId}
        schoolId={schoolid}
        assessmentIds={assessmentIds}
        selectedStudentIds={selectedStudentIds}
        isParentView={isParentView}
        isTeacherView={isTeacherView}
        isDetailedView={isDetailedView}
        activeAccordionStudents={activeAccordionStudents}
      />
    </>
  );
};

export default ShareActionCard;
